import { useEffect, useState } from 'react';
import type { PartnerListItem, PartnerReferralListItem, PartnerReferralSourceType, PartnerReferralStatus } from '@accestra/types';
import {
  Button,
  C,
  Card,
  Field,
  Pill,
  TableWrap,
  asNullableText,
  friendlyError,
  formatMaybeDateTime,
  inputStyle,
  paFetch,
  statusColor,
  tdS,
  textareaStyle,
  thS,
  titleCase,
} from './shared';

const REFERRAL_SOURCE_OPTIONS: PartnerReferralSourceType[] = [
  'manual',
  'founder_assigned',
  'partner_submitted',
  'signup_form',
];

const REFERRAL_STATUS_OPTIONS: Array<PartnerReferralStatus | 'all'> = [
  'all',
  'lead',
  'qualified',
  'trial',
  'customer',
  'closed_lost',
];

interface PartnerListResponse {
  partners: PartnerListItem[];
  total: number;
}

interface ReferralListResponse {
  referrals: PartnerReferralListItem[];
  total: number;
}

function emptyReferralForm() {
  return {
    partnerId: '',
    organisationId: '',
    prospectName: '',
    prospectEmail: '',
    prospectCompany: '',
    sourceType: 'manual' as PartnerReferralSourceType,
    referralCode: '',
    status: 'lead' as PartnerReferralStatus,
    notes: '',
    allowOverride: false,
  };
}

export default function ReferralsPanel() {
  const [partners, setPartners] = useState<PartnerListItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<PartnerReferralStatus | 'all'>('all');
  const [referrals, setReferrals] = useState<PartnerReferralListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState(emptyReferralForm);
  const [editForm, setEditForm] = useState(emptyReferralForm);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const selectedReferral = referrals.find((referral) => referral.id === selectedId) ?? null;

  async function loadPartners() {
    try {
      const query = new URLSearchParams({ page: '1', pageSize: '100' });
      const result = await paFetch<PartnerListResponse>('GET', `/platform-admin/partners?${query.toString()}`);
      setPartners(result.partners);
      if (!createForm.partnerId && result.partners[0]) {
        setCreateForm((current) => ({ ...current, partnerId: result.partners[0]!.id }));
      }
    } catch (nextError) {
      setError(friendlyError(nextError));
    }
  }

  async function loadReferrals() {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        page: '1',
        pageSize: '100',
      });
      if (statusFilter !== 'all') {
        query.set('status', statusFilter);
      }

      const result = await paFetch<ReferralListResponse>('GET', `/platform-admin/partner-referrals?${query.toString()}`);
      setReferrals(result.referrals);
      if (!result.referrals.find((referral) => referral.id === selectedId)) {
        setSelectedId(result.referrals[0]?.id ?? null);
      }
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPartners();
  }, []);

  useEffect(() => {
    void loadReferrals();
  }, [statusFilter]);

  useEffect(() => {
    if (!selectedReferral) {
      return;
    }

    setEditForm({
      partnerId: selectedReferral.partnerId,
      organisationId: selectedReferral.organisationId ?? '',
      prospectName: selectedReferral.prospectName ?? '',
      prospectEmail: selectedReferral.prospectEmail ?? '',
      prospectCompany: selectedReferral.prospectCompany ?? '',
      sourceType: selectedReferral.sourceType,
      referralCode: selectedReferral.referralCode ?? '',
      status: selectedReferral.status,
      notes: selectedReferral.notes ?? '',
      allowOverride: false,
    });
  }, [selectedReferral]);

  async function createReferral() {
    setBusyAction('create');
    setFeedback(null);
    setError(null);
    try {
      await paFetch('POST', '/platform-admin/partner-referrals', {
        partnerId: createForm.partnerId,
        organisationId: asNullableText(createForm.organisationId),
        prospectName: asNullableText(createForm.prospectName),
        prospectEmail: asNullableText(createForm.prospectEmail),
        prospectCompany: asNullableText(createForm.prospectCompany),
        sourceType: createForm.sourceType,
        referralCode: asNullableText(createForm.referralCode),
        status: createForm.status,
        notes: asNullableText(createForm.notes),
        allowOverride: createForm.allowOverride,
      });
      setCreateForm((current) => ({
        ...emptyReferralForm(),
        partnerId: current.partnerId,
      }));
      await loadReferrals();
      setFeedback('Manual referral created.');
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  async function updateReferral() {
    if (!selectedReferral) {
      return;
    }

    setBusyAction('update');
    setFeedback(null);
    setError(null);
    try {
      await paFetch('PATCH', `/platform-admin/partner-referrals/${selectedReferral.id}`, {
        partnerId: editForm.partnerId,
        organisationId: asNullableText(editForm.organisationId),
        prospectName: asNullableText(editForm.prospectName),
        prospectEmail: asNullableText(editForm.prospectEmail),
        prospectCompany: asNullableText(editForm.prospectCompany),
        sourceType: editForm.sourceType,
        referralCode: asNullableText(editForm.referralCode),
        status: editForm.status,
        notes: asNullableText(editForm.notes),
        allowOverride: editForm.allowOverride,
      });
      await loadReferrals();
      setFeedback('Referral updated.');
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <Card
        title="Manual referral"
        subtitle="Create founder-managed referrals, including manual organisation attribution when a referral code was missed."
      >
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 12px', color: C.danger, fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}
        {feedback && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '10px 12px', color: C.success, fontSize: 13, marginBottom: 12 }}>
            {feedback}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
          <Field label="Partner">
            <select style={inputStyle} value={createForm.partnerId} onChange={(event) => setCreateForm((current) => ({ ...current, partnerId: event.target.value }))}>
              {partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}
            </select>
          </Field>
          <Field label="Organisation id (optional)">
            <input style={inputStyle} value={createForm.organisationId} onChange={(event) => setCreateForm((current) => ({ ...current, organisationId: event.target.value }))} />
          </Field>
          <Field label="Status">
            <select style={inputStyle} value={createForm.status} onChange={(event) => setCreateForm((current) => ({ ...current, status: event.target.value as PartnerReferralStatus }))}>
              {REFERRAL_STATUS_OPTIONS.filter((option) => option !== 'all').map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
            </select>
          </Field>
          <Field label="Prospect company">
            <input style={inputStyle} value={createForm.prospectCompany} onChange={(event) => setCreateForm((current) => ({ ...current, prospectCompany: event.target.value }))} />
          </Field>
          <Field label="Prospect name">
            <input style={inputStyle} value={createForm.prospectName} onChange={(event) => setCreateForm((current) => ({ ...current, prospectName: event.target.value }))} />
          </Field>
          <Field label="Prospect email">
            <input style={inputStyle} value={createForm.prospectEmail} onChange={(event) => setCreateForm((current) => ({ ...current, prospectEmail: event.target.value }))} />
          </Field>
          <Field label="Source type">
            <select style={inputStyle} value={createForm.sourceType} onChange={(event) => setCreateForm((current) => ({ ...current, sourceType: event.target.value as PartnerReferralSourceType }))}>
              {REFERRAL_SOURCE_OPTIONS.map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
            </select>
          </Field>
          <Field label="Referral code">
            <input style={inputStyle} value={createForm.referralCode} onChange={(event) => setCreateForm((current) => ({ ...current, referralCode: event.target.value }))} />
          </Field>
          <Field label="Override conflicting attribution">
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 42 }}>
              <input
                type="checkbox"
                checked={createForm.allowOverride}
                onChange={(event) => setCreateForm((current) => ({ ...current, allowOverride: event.target.checked }))}
              />
              <span style={{ color: C.text, fontSize: 13 }}>Allow override</span>
            </label>
          </Field>
        </div>

        <div style={{ marginTop: 12 }}>
          <Field label="Notes">
            <textarea style={textareaStyle} value={createForm.notes} onChange={(event) => setCreateForm((current) => ({ ...current, notes: event.target.value }))} />
          </Field>
        </div>

        <div style={{ marginTop: 16 }}>
          <Button variant="primary" disabled={busyAction !== null || !createForm.partnerId} onClick={() => void createReferral()}>
            {busyAction === 'create' ? 'Creating…' : 'Create referral'}
          </Button>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(360px, 0.95fr)', gap: 18 }}>
        <Card
          title="Referrals"
          subtitle="Review current attribution state and open a referral to update status or organisation linking."
          action={(
            <div style={{ display: 'flex', gap: 8 }}>
              <select style={inputStyle} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as PartnerReferralStatus | 'all')}>
                {REFERRAL_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status === 'all' ? 'All statuses' : titleCase(status)}
                  </option>
                ))}
              </select>
              <Button onClick={() => void loadReferrals()}>Refresh</Button>
            </div>
          )}
        >
          {loading ? (
            <div style={{ color: C.muted, fontSize: 13 }}>Loading referrals…</div>
          ) : (
            <TableWrap>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thS}>Prospect</th>
                    <th style={thS}>Partner</th>
                    <th style={thS}>Status</th>
                    <th style={thS}>Attributed</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ ...tdS, textAlign: 'center', color: C.muted }}>No referrals found.</td>
                    </tr>
                  ) : referrals.map((referral) => (
                    <tr
                      key={referral.id}
                      onClick={() => setSelectedId(referral.id)}
                      style={{
                        cursor: 'pointer',
                        background: selectedId === referral.id ? 'rgba(14, 165, 164, 0.06)' : 'transparent',
                      }}
                    >
                      <td style={tdS}>
                        <div style={{ fontWeight: 760 }}>{referral.prospectCompany || referral.organisationName || 'Manual referral'}</div>
                        <div style={{ color: C.muted, fontSize: 12 }}>{referral.prospectEmail || referral.organisationSlug || 'No email'}</div>
                      </td>
                      <td style={tdS}>{referral.partnerName}</td>
                      <td style={tdS}><Pill label={titleCase(referral.status)} tone={statusColor(referral.status)} /></td>
                      <td style={tdS}>{formatMaybeDateTime(referral.attributedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          )}
        </Card>

        <Card
          title="Referral detail"
          subtitle="Update status, reassign the partner, or link the referral to an organisation when needed."
        >
          {!selectedReferral ? (
            <div style={{ color: C.muted, fontSize: 13 }}>Select a referral to edit it.</div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 780, color: C.text }}>{selectedReferral.prospectCompany || selectedReferral.organisationName || 'Referral detail'}</div>
                  <div style={{ color: C.muted, fontSize: 13 }}>{selectedReferral.partnerName}</div>
                </div>
                <Pill label={titleCase(selectedReferral.status)} tone={statusColor(selectedReferral.status)} />
              </div>

              <Field label="Partner">
                <select style={inputStyle} value={editForm.partnerId} onChange={(event) => setEditForm((current) => ({ ...current, partnerId: event.target.value }))}>
                  {partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}
                </select>
              </Field>
              <Field label="Organisation id">
                <input style={inputStyle} value={editForm.organisationId} onChange={(event) => setEditForm((current) => ({ ...current, organisationId: event.target.value }))} />
              </Field>
              <Field label="Status">
                <select style={inputStyle} value={editForm.status} onChange={(event) => setEditForm((current) => ({ ...current, status: event.target.value as PartnerReferralStatus }))}>
                  {REFERRAL_STATUS_OPTIONS.filter((option) => option !== 'all').map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
                </select>
              </Field>
              <Field label="Source type">
                <select style={inputStyle} value={editForm.sourceType} onChange={(event) => setEditForm((current) => ({ ...current, sourceType: event.target.value as PartnerReferralSourceType }))}>
                  {REFERRAL_SOURCE_OPTIONS.map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
                </select>
              </Field>
              <Field label="Referral code">
                <input style={inputStyle} value={editForm.referralCode} onChange={(event) => setEditForm((current) => ({ ...current, referralCode: event.target.value }))} />
              </Field>
              <Field label="Prospect company">
                <input style={inputStyle} value={editForm.prospectCompany} onChange={(event) => setEditForm((current) => ({ ...current, prospectCompany: event.target.value }))} />
              </Field>
              <Field label="Prospect name">
                <input style={inputStyle} value={editForm.prospectName} onChange={(event) => setEditForm((current) => ({ ...current, prospectName: event.target.value }))} />
              </Field>
              <Field label="Prospect email">
                <input style={inputStyle} value={editForm.prospectEmail} onChange={(event) => setEditForm((current) => ({ ...current, prospectEmail: event.target.value }))} />
              </Field>
              <Field label="Notes">
                <textarea style={textareaStyle} value={editForm.notes} onChange={(event) => setEditForm((current) => ({ ...current, notes: event.target.value }))} />
              </Field>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={editForm.allowOverride} onChange={(event) => setEditForm((current) => ({ ...current, allowOverride: event.target.checked }))} />
                <span style={{ color: C.text, fontSize: 13 }}>Allow override if another referral already owns this organisation.</span>
              </label>
              <div>
                <Button variant="primary" disabled={busyAction !== null} onClick={() => void updateReferral()}>
                  {busyAction === 'update' ? 'Saving…' : 'Save referral'}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
